import React, { useState, useEffect, useRef, useCallback } from "react";
import { Page, Grid, Card, Button } from "tabler-react";
import SiteWrapper from "../../SiteWrapper";
import * as ticketService from "../../store/services/ticketServices";

const STATUS_LABELS = {
    open: "Open",
    waiting: "Waiting",
    on_hold: "On Hold",
    closed: "Closed",
};

const STATUS_COLORS = {
    open: { background: "#e8f5f5", color: "#2B9A99" },
    waiting: { background: "#fff8e1", color: "#f6a821" },
    on_hold: { background: "#f0f0f0", color: "#666" },
    closed: { background: "#fce8e8", color: "#cd201f" },
};

function StatusBadge({ status }) {
    const style = {
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        ...(STATUS_COLORS[status] || {}),
    };
    return <span style={style}>{STATUS_LABELS[status] || status}</span>;
}

function ViewTicket(props) {
    const ticketId = props.match.params.id;
    const [user] = useState(
        (localStorage.getItem("user") && JSON.parse(localStorage.getItem("user"))) || {}
    );
    const isStaff = user?.type === "Super" || user?.type === "Admin";

    const [loading, setLoading] = useState(true);
    const [ticket, setTicket] = useState(null);
    const [replyBody, setReplyBody] = useState("");
    const [replying, setReplying] = useState(false);
    const [replyError, setReplyError] = useState("");
    const [statusUpdating, setStatusUpdating] = useState(false);
    const bottomRef = useRef(null);

    const loadTicket = useCallback(() => {
        ticketService
            .getTicket(ticketId)
            .then((res) => {
                setTicket(res.data?.data);
                setLoading(false);
                ticketService.markTicketRead(ticketId).catch(() => {});
            })
            .catch(() => setLoading(false));
    }, [ticketId]);

    useEffect(() => {
        loadTicket();

        const interval = setInterval(loadTicket, 30000);
        return () => clearInterval(interval);
    }, [loadTicket]);

    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [ticket?.replies?.length]);

    const handleReply = (e) => {
        e.preventDefault();
        if (!replyBody.trim()) return;

        setReplying(true);
        setReplyError("");
        ticketService
            .replyToTicket(ticketId, replyBody)
            .then((res) => {
                setTicket(res.data?.data);
                setReplyBody("");
                setReplying(false);
            })
            .catch((err) => {
                setReplyError(err?.response?.data?.message || "Failed to send reply.");
                setReplying(false);
            });
    };

    const handleStatusChange = (status) => {
        setStatusUpdating(true);
        ticketService
            .updateTicketStatus(ticketId, status)
            .then((res) => {
                setTicket((prev) => ({ ...prev, status: res.data?.data?.status, closed_at: res.data?.data?.closed_at }));
                setStatusUpdating(false);
            })
            .catch(() => setStatusUpdating(false));
    };

    const formatDateTime = (ts) => {
        if (!ts) return "—";
        const d = new Date(ts);
        return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getFullYear()).slice(-2)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    };

    const isOwnerMessage = (reply) => reply.user_id === ticket?.owner_id;

    return (
        <SiteWrapper loading={loading}>
            <Page.Content>
                <div className="page-header">
                    <h1 className="page-title">
                        {ticket ? ticket.subject : "Ticket"}
                    </h1>
                    <div className="page-options">
                        <Button color="secondary" onClick={() => props.history.push("/tickets")}>
                            Back
                        </Button>
                    </div>
                </div>

                {ticket && (
                    <Grid.Row>
                        <Grid.Col md={8}>
                            <Card>
                                <Card.Header>
                                    <Card.Title>Conversation</Card.Title>
                                    <Card.Options>
                                        <StatusBadge status={ticket.status} />
                                    </Card.Options>
                                </Card.Header>
                                <Card.Body>
                                    {ticket.status === "waiting" && !isStaff && (
                                        <div style={{
                                            background: "#fff8e1", border: "1px solid #f6a821",
                                            borderRadius: 8, padding: "10px 14px", marginBottom: 16,
                                            fontSize: 13, color: "#856404",
                                        }}>
                                            Our team has replied — please review the message below and respond if needed.
                                        </div>
                                    )}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                        {(ticket.replies || []).map((reply) => {
                                            const isOwner = isOwnerMessage(reply);
                                            return (
                                                <div
                                                    key={reply.id}
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: isOwner ? "flex-end" : "flex-start",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            maxWidth: "75%",
                                                            background: isOwner ? "#e8f5f5" : "#f5f5f5",
                                                            borderRadius: 12,
                                                            padding: "10px 14px",
                                                        }}
                                                    >
                                                        <div style={{ fontSize: 12, color: "#9aa0ac", marginBottom: 4 }}>
                                                            <strong style={{ color: "#333" }}>
                                                                {reply.user?.name} {reply.user?.last_name}
                                                            </strong>
                                                            {" · "}
                                                            {formatDateTime(reply.created_at)}
                                                        </div>
                                                        <div style={{ whiteSpace: "pre-wrap" }}>{reply.body}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div ref={bottomRef} />
                                    </div>
                                </Card.Body>
                                {ticket.status !== "closed" && (
                                    <Card.Footer>
                                        {replyError && (
                                            <div className="alert alert-danger mb-3">{replyError}</div>
                                        )}
                                        <form onSubmit={handleReply}>
                                            <textarea
                                                className="form-control"
                                                rows={6}
                                                value={replyBody}
                                                onChange={(e) => setReplyBody(e.target.value)}
                                                placeholder="Write a reply..."
                                                style={{ marginBottom: 10 }}
                                            />
                                            <Button type="submit" className="btn-mm" disabled={replying || !replyBody.trim()}>
                                                {replying ? "Sending..." : "Send Reply"}
                                            </Button>
                                        </form>
                                    </Card.Footer>
                                )}
                            </Card>
                        </Grid.Col>

                        <Grid.Col md={4}>
                            <Card>
                                <Card.Header>
                                    <Card.Title>Details</Card.Title>
                                </Card.Header>
                                <Card.Body>
                                    <table style={{ width: "100%", fontSize: 14, lineHeight: 2 }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ color: "#9aa0ac", paddingRight: 12 }}>Status</td>
                                                <td><StatusBadge status={ticket.status} /></td>
                                            </tr>
                                            <tr>
                                                <td style={{ color: "#9aa0ac" }}>Priority</td>
                                                <td style={{ textTransform: "capitalize" }}>{ticket.priority}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ color: "#9aa0ac" }}>Category</td>
                                                <td style={{ textTransform: "capitalize" }}>{ticket.category}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ color: "#9aa0ac" }}>Opened</td>
                                                <td>{formatDateTime(ticket.created_at)}</td>
                                            </tr>
                                            {isStaff && ticket.owner && (
                                                <tr>
                                                    <td style={{ color: "#9aa0ac" }}>From</td>
                                                    <td>
                                                        {ticket.owner.name} {ticket.owner.last_name}
                                                        <div style={{ fontSize: 11, color: "#9aa0ac" }}>{ticket.owner.email}</div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </Card.Body>
                                {isStaff && (
                                    <Card.Footer>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                            <label style={{ fontSize: 12, color: "#9aa0ac", marginBottom: 2 }}>Change Status</label>
                                            <select
                                                className="form-control"
                                                value={ticket.status}
                                                disabled={statusUpdating}
                                                onChange={(e) => handleStatusChange(e.target.value)}
                                            >
                                                <option value="open">Open</option>
                                                <option value="waiting">Waiting</option>
                                                <option value="on_hold">On Hold</option>
                                                <option value="closed">Closed</option>
                                            </select>
                                        </div>
                                    </Card.Footer>
                                )}
                            </Card>

                            {ticket.meta && (
                                <div style={{ marginTop: 16 }}>
                                <Card>
                                    <Card.Header>
                                        <Card.Title>Device Info</Card.Title>
                                    </Card.Header>
                                    <Card.Body>
                                        <table style={{ width: "100%", fontSize: 14, lineHeight: 2 }}>
                                            <tbody>
                                                {ticket.meta.os && (
                                                    <tr>
                                                        <td style={{ color: "#9aa0ac", paddingRight: 12 }}>Platform</td>
                                                        <td style={{ textTransform: "uppercase" }}>{ticket.meta.os}</td>
                                                    </tr>
                                                )}
                                                {ticket.meta.os_version && (
                                                    <tr>
                                                        <td style={{ color: "#9aa0ac", paddingRight: 12 }}>OS Version</td>
                                                        <td>{ticket.meta.os_version}</td>
                                                    </tr>
                                                )}
                                                {ticket.meta.device && (
                                                    <tr>
                                                        <td style={{ color: "#9aa0ac", paddingRight: 12 }}>Device</td>
                                                        <td>{ticket.meta.device}</td>
                                                    </tr>
                                                )}
                                                {ticket.meta.app_version && (
                                                    <tr>
                                                        <td style={{ color: "#9aa0ac", paddingRight: 12 }}>App Version</td>
                                                        <td>{ticket.meta.app_version}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </Card.Body>
                                </Card>
                                </div>
                            )}
                        </Grid.Col>
                    </Grid.Row>
                )}
            </Page.Content>
        </SiteWrapper>
    );
}

export default ViewTicket;
